// RenderedModeToggle.tsx — the rendered-mode `Switch` + its "this re-crawls" confirm dialog,
// extracted (review round 1, source-detail-diagnostics Task 5) out of `BotSources.tsx` so
// `SourceDetail.tsx` doesn't duplicate it. Review's product argument for why the detail screen
// needs this as a live control rather than a static label: the most likely visitor to this page
// just watched a truncated or near-empty crawl and read a message telling them to turn rendered
// mode on — their next action is "turn it on and resync," and this page already carries Resync,
// so making the fix a navigation away from the diagnosis would be the wrong shape.
//
// Presentation only — the actual mutation (PATCH `renderedMode`, which the API always turns into
// a resync) stays with the caller, since `BotSources.tsx` and `SourceDetail.tsx` have different
// mutation objects (one keyed by row id in a shared map, one scoped to a single source) and
// different override/polling wiring around them.
import { useState } from 'react';
import { CircleHelpIcon } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components/popover';
import { Switch } from '@repo/ui/components/switch';
import type { Catalog } from '../locales/en';

/** Spec §A1 (hygiene-cycle design) — verbatim copy. Radix Popover (not Tooltip) because
 * Tooltip doesn't open on tap, and this has to work on the phones Johnny tests on. */
export function RenderedModeExplainer({ t }: { t: Catalog }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t.sources.renderedModeExplainerAriaLabel}
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
        >
          <CircleHelpIcon className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-2 text-[12.5px] font-normal normal-case">
        <p>
          <strong>{t.sources.renderedModeExplainerBody1Bold}</strong>
          {t.sources.renderedModeExplainerBody1Rest}
        </p>
        <p>{t.sources.renderedModeExplainerBody2}</p>
        <p>{t.sources.renderedModeExplainerBody3}</p>
      </PopoverContent>
    </Popover>
  );
}

/** Handoff ask (BotSources.tsx's original dialog copy, unchanged): toggling rendered mode
 * re-triggers a full resync (see `crawlSource`'s own doc comment,
 * apps/api/src/jobs/handlers/crawl-source.ts) — it doesn't wipe the index the instant you flip
 * the switch, but it re-crawls with the new renderer, replaces whatever content comes back
 * different, and drops whatever no longer appears once that finishes. That's real enough to name
 * before it happens; the current index stays queryable until the new crawl completes, so it's a
 * re-index, not a blackout. */
export function RenderedModeToggle({
  checked,
  disabled,
  onConfirm,
  t,
}: {
  checked: boolean;
  disabled?: boolean;
  onConfirm: (value: boolean) => void;
  t: Catalog;
}) {
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const mode = pendingValue ? t.sources.modeRendered : t.sources.modeStandard;

  return (
    <>
      <Switch
        checked={checked}
        disabled={disabled}
        aria-label={t.sources.renderedModeLabel}
        onCheckedChange={(value) => setPendingValue(value)}
      />
      <Dialog open={pendingValue !== null} onOpenChange={(open) => !open && setPendingValue(null)}>
        <DialogContent closeLabel={t.common.close}>
          <DialogHeader>
            <DialogTitle>{t.sources.switchModeTitle({ mode })}</DialogTitle>
            <DialogDescription>{t.sources.switchModeDescription({ mode })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPendingValue(null)}>
              {t.sources.cancelCta}
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (pendingValue !== null) onConfirm(pendingValue);
                setPendingValue(null);
              }}
            >
              {t.sources.switchModeCta({ mode })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
